/**
 * The `org_units` a result layer paints, and which results have none on screen — the shared
 * half of §F6's result layers, read by both `ResultLayers` (the paint) and the §F5.6 legend
 * block (e) (the dimmed row).
 *
 * A hook rather than a store slice, for the same reason `choroClasses.ts` is one (NOTES
 * #190): the input is already shared, cached server state — the very `['units', …]` key the
 * §F5.3 choropleth holds — so a second caller costs one `useMemo`, no fetch and no render
 * pass, where a slice would mean one component writing state from an effect and another
 * reading it a frame later.
 *
 * **Why results are drawn from the explorer's own polygons at all.** They could be fetched
 * per result, keyed by the ids the run named. They are not, because then a result outline
 * would be drawn from geometry at a different generalisation from the boundary underneath
 * it, and a 3.4 px outline sitting a pixel off its own commune is exactly the kind of
 * detail that makes a reader distrust the number beside it. The price is that a result is
 * paintable only while its units are in the current geography — and the price is paid as a
 * **dimmed legend row, never a deletion**: a country change must not throw away an analysis
 * the reader waited for.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchUnits, queryKeys, unitsQueryFor, type UnitsFC } from '../../lib/api';
import { selectScopeParent, useAppStore, type ResultLayerSpec } from '../../lib/state';
// The one definition of the neutral the UC1 chips and this hatch share — a constants-only
// leaf module, so the map does not take a dependency on the Intelligence components.
import { NEUTRAL_COLOR } from '../intelligence/uc1Constants';

/**
 * The `org_units` payload the explorer is currently drawn from.
 *
 * `enabled` is the caller's "is there anything to paint": with no result layers this must
 * not put a `/hierarchy/units` request on the wire that the explorer was not already making.
 */
export function useResultUnits(enabled: boolean): UnitsFC | undefined {
  const level = useAppStore((s) => s.reportingLevel);
  const country = useAppStore((s) => s.country);
  const parent = useAppStore(selectScopeParent);
  const query = unitsQueryFor(level);
  const { data } = useQuery({
    queryKey: queryKeys.units(query.type, query.level, country, 'z7', parent),
    queryFn: () => fetchUnits(query.type, query.level, country, 'z7', parent),
    enabled,
  });
  return data;
}

/** The unit ids a spec wants painted — `unit_ids` for `units`, `values`' keys for a choropleth. */
export function specUnitIds(spec: ResultLayerSpec): string[] {
  if (spec.kind === 'units') return spec.unit_ids ?? [];
  if (spec.kind === 'choropleth') return Object.keys(spec.values ?? {});
  return [];
}

/**
 * Which results have **no** unit in the geography now on screen — §F5.6 block (e)'s dimmed
 * rows.
 *
 * An artefact-backed result (`geojson`/`points`) is never in this set: its geometry is its
 * own and does not depend on the level or the country the reader is on.
 */
export function useOutOfViewResults(): Set<string> {
  const results = useAppStore((s) => s.results);
  const units = useResultUnits(results.length > 0);

  return useMemo(() => {
    const out = new Set<string>();
    // No payload yet is not "out of view" — it is "not answered yet", and a legend that
    // called a result out of view for the length of a fetch would print a falsehood about
    // the reader's own work (the rule block (b) applies to "no values reported").
    if (!units) return out;
    const present = new Set(units.features.map((f) => f.properties.id));
    for (const result of results) {
      const wanted = specUnitIds(result.spec);
      if (wanted.length && !wanted.some((id) => present.has(id))) out.add(result.id);
    }
    return out;
  }, [results, units]);
}

/* ------------------------------------------------------- §H1 `units` paint (shared) */

/**
 * Client round 30 Sep 2026 (NOTES #728): a `units` result is a *filled* choropleth — the
 * priority / constraint class is the fill, not the outline — so the fill is near-solid and
 * the outline thin. (§H1's outline-over-fill reading is retired: a use case clears every
 * other layer when it opens, so nothing sits under the fill any more.)
 */
export const UNITS_WEIGHT = 1.2;
export const UNITS_FILL_OPACITY = 0.85;
/**
 * The weight of a `spec.outlines` outline — UC2's launch round (design §3) draws the
 * selected watershed this way on every chip. Thick enough to read over a solid fill at the
 * dock's own zoom, which 1.2 px is not.
 */
export const UNITS_OUTLINE_WEIGHT = 3.4;
/**
 * A `spec.hairline` unit's boundary — the transparent, clickable watershed layer a raster
 * chip draws in place of its choropleth (client round 2 Oct 2026). One thin neutral line:
 * enough to say where a watershed ends, little enough that eleven of them over a
 * land-cover raster are a set of boundaries rather than a second map.
 */
export const BOUNDARY_WEIGHT = 1;
export const BOUNDARY_COLOR = '#55614F';
export const BOUNDARY_OPACITY = 0.7;
/** The CMP colour a `units` row falls back to when the server named none. */
export const UNITS_FALLBACK_COLOR = '#22301F';

/**
 * The SVG pattern a not-assessed unit is filled with, and the class that reaches it.
 *
 * The pattern itself is injected once per map by `ResultLayers`; the class is what
 * `base.css` turns into `fill: url(#intel-hatch)`. Leaflet has no pattern support of its
 * own — a `fillColor` is a colour string and nothing else — so the hatch has to travel as
 * a class name on the path and be resolved in CSS.
 */
export const HATCH_PATTERN_ID = 'intel-hatch';
export const HATCHED_CLASS = 'unit-hatched';
/**
 * The light ground the hatch is drawn over. Higher than `UNITS_FILL_OPACITY` because the
 * hatch's own lines are thin: at .12 the unit would read as unpainted rather than as
 * deliberately blank.
 */
export const HATCHED_FILL_OPACITY = 0.9;
/**
 * The hatch's own ground colour — literally the "not flagged" neutral, imported rather
 * than copied.
 *
 * It was a copy (`#d9ded9`), and the copy went stale on 3 October 2026 when the client
 * had that grey darkened for being indistinguishable from a gap in the map. The hatch kept
 * the old value, so "not assessed" came out *lighter* than "not flagged" — the two classes
 * ranked backwards against each other on the one map that shows both.
 */
export const HATCHED_FILL_COLOR = NEUTRAL_COLOR;

/** A Leaflet `PathOptions` subset, spelled here so this module needs no Leaflet import. */
export interface UnitPathStyle {
  color: string;
  weight: number;
  opacity: number;
  fillColor: string;
  fillOpacity: number;
  /** Present only for a hatched unit — `ResultLayers` passes it straight to Leaflet. */
  className?: string;
}

/**
 * One unit's paint for a `units` result layer: its own colour from `spec.colors`, unless
 * `spec.patterns` marks it hatched, in which case the colour is dropped for the neutral
 * ground and the hatch class rides along.
 *
 * Split out of `ResultLayers` so the three-way decision (colour / fallback / hatch) can be
 * tested without a Leaflet map, which is the only part of that component with a rule in it.
 */
export function unitsPathStyle(spec: ResultLayerSpec, id: string): UnitPathStyle {
  // An outline is drawn *over* whatever this unit's fill already is — a hatch included —
  // so it is read first and applied to both branches below: the selected watershed must
  // read as selected even when the chip could not assess it.
  const outline = spec.outlines?.[id];

  // An outline-only layer draws the boundary and nothing else — whatever is under it (a
  // raster, on UC2's land-cover chip) stays readable.
  if (spec.outlineOnly) {
    // A hairline layer's *unoutlined* units are boundaries; the one the spec outlines is
    // the selection, and it keeps the thick line whatever the rest of the layer is.
    const hairline = spec.hairline && !outline;
    const color = outline ?? (hairline ? BOUNDARY_COLOR : (spec.colors?.[id] ?? UNITS_FALLBACK_COLOR));
    return {
      color,
      weight: hairline ? BOUNDARY_WEIGHT : UNITS_OUTLINE_WEIGHT,
      opacity: hairline ? BOUNDARY_OPACITY : 1,
      fillColor: color,
      // Zero, never `fill: false`: `ResultLayers` hit-tests the interior, and this layer
      // exists to be clicked.
      fillOpacity: 0,
    };
  }

  if (spec.patterns?.[id] === 'hatched') {
    return {
      color: outline ?? HATCHED_FILL_COLOR,
      weight: outline ? UNITS_OUTLINE_WEIGHT : UNITS_WEIGHT,
      opacity: 1,
      fillColor: HATCHED_FILL_COLOR,
      fillOpacity: HATCHED_FILL_OPACITY,
      className: HATCHED_CLASS,
    };
  }
  const color = spec.colors?.[id] ?? UNITS_FALLBACK_COLOR;
  return {
    color: outline ?? color,
    weight: outline ? UNITS_OUTLINE_WEIGHT : UNITS_WEIGHT,
    opacity: 1,
    fillColor: color,
    fillOpacity: spec.fillOpacity ?? UNITS_FILL_OPACITY,
  };
}
